/**
 * MatchClock: マッチ時計の単一描画ポイント。 旧 Play.tsx に直書きされていた
 * WholePolicyInline / ClockBar / ClockSide / PerBoardPolicyChip / ClockChip を集約。
 *
 * `<MatchClock variant="inline" />` … タイトルバー右に置く 1 行の WholePolicy 表示
 * `<MatchClock variant="bar" />`    … 盤上に固定する PerBoard 用の横長 bar
 * `<MatchClock variant="tab" boardId={n} />`  … タブカード下の小さな chip
 *
 * いずれも useRoom().clock の最新値を 250ms / 500ms 周期で再描画する。
 *
 * 残時間 0 検出時の自動 ClaimTimeout は inline / bar 両方で 1 度だけ送る (旧実装は
 * bar のみ送っていて、 WholePolicy 戦ではサーバが時間切れを知らないまま
 * 放置される UX 不具合があった)。 共通 hook `useAutoClaimTimeout` で扱う。
 */

import { useEffect, useState } from 'react';
import type { Side } from '../games/animal';
import { useRoom } from '../state/room';

export interface ClockSnapshotJson {
  at_ms: number;
  boards: { board: number; host_ms: number; guest_ms: number; running: Side | null }[];
  flagged: [number, Side] | null;
}

type Variant =
  | { kind: 'inline'; you: 'host' | 'guest' | 'spectator' | null; frozen: boolean }
  /**
   * 盤の上下に置く 1 人ぶんの残時間。 which で自分/相手を選ぶ。
   * boardId を渡すとその盤の時計 (PerBoardPolicy)、 省略すると マッチ全体 (WholePolicy)。
   */
  | {
      kind: 'seat';
      you: 'host' | 'guest' | 'spectator' | null;
      which: 'mine' | 'opp';
      frozen: boolean;
      boardId?: number;
      yourSide?: Side | null;
    }
  | { kind: 'bar'; yourSide: Side | null; hostName: string; guestName: string; frozen: boolean }
  | { kind: 'tab'; boardId: number; yourSide: Side | null; frozen: boolean };

export function MatchClock(props: Variant) {
  switch (props.kind) {
    case 'inline':
      return <WholePolicyInline you={props.you} frozen={props.frozen} />;
    case 'seat':
      return (
        <SeatClock
          you={props.you}
          which={props.which}
          frozen={props.frozen}
          boardId={props.boardId}
          yourSide={props.yourSide ?? null}
        />
      );
    case 'bar':
      return (
        <ClockBar
          yourSide={props.yourSide}
          hostName={props.hostName}
          guestName={props.guestName}
          frozen={props.frozen}
        />
      );
    case 'tab':
      return (
        <PerBoardPolicyChip
          boardId={props.boardId}
          yourSide={props.yourSide}
          frozen={props.frozen}
        />
      );
  }
}

function useTicker(frozen: boolean, intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (frozen) return;
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [frozen, intervalMs]);
  return now;
}

/**
 * いずれかの盤で running 側の残時間が 0 を切ったら、 1 度だけ `claim_timeout` を
 * サーバに送る。 frozen が立ち下がるたびに claimed フラグをリセット (= 再戦などに対応)。
 *
 * WholePolicy / PerBoardPolicy どちらの clock snapshot でも形式が同じなので
 * inline と bar で共通利用できる。
 *
 * **CPU 戦 (`isLocal=true`) は無効化**: ユーザーの仕様で CPU 対戦は時間切れで強制終了
 * させない (= 時計表示は 0 のまま続行できる)。
 */
function useAutoClaimTimeout(clock: ClockSnapshotJson | null, frozen: boolean, now: number) {
  const claimTimeout = useRoom((s) => s.claimTimeout);
  const isLocal = useRoom((s) => s.isLocal);
  const [claimed, setClaimed] = useState(false);
  useEffect(() => {
    if (frozen || isLocal || !clock?.boards || claimed) return;
    const someoneFlagged = clock.boards.some((b) => {
      if (b.running === 'First') return b.host_ms - Math.max(0, now - clock.at_ms) <= 0;
      if (b.running === 'Second') return b.guest_ms - Math.max(0, now - clock.at_ms) <= 0;
      return false;
    });
    if (someoneFlagged) {
      setClaimed(true);
      claimTimeout();
    }
  }, [clock, now, frozen, isLocal, claimed, claimTimeout]);
  useEffect(() => {
    if (frozen) setClaimed(false);
  }, [frozen]);
}

function WholePolicyInline({
  you,
  frozen,
}: {
  you: 'host' | 'guest' | 'spectator' | null;
  frozen: boolean;
}) {
  const clock = useRoom((s) => s.clock) as ClockSnapshotJson | null;
  const now = useTicker(frozen, 250);
  // 残時間 0 で自動 claim_timeout (= サーバに時間切れを通知して finish_match に遷移)。
  // 旧実装は bar (PerBoardPolicy) のみで送っていて、 WholePolicy 戦では
  // サーバが timeout を知らないままだった。 観戦者 (you=spectator) でも害無いので一律送信。
  useAutoClaimTimeout(clock, frozen, now);
  if (!clock?.boards?.length) return null;
  const c = clock.boards[0];
  const myMs = you === 'host' ? c.host_ms : c.guest_ms;
  const oppMs = you === 'host' ? c.guest_ms : c.host_ms;
  const myRunning = !frozen && c.running === (you === 'host' ? 'First' : 'Second');
  const oppRunning = !frozen && c.running === (you === 'host' ? 'Second' : 'First');
  return (
    <span className="play-title-clock" data-testid="play-title-clock">
      <ClockChip ms={myMs} running={myRunning} atMs={clock.at_ms} now={now} mine />
      <span className="play-title-clock-sep">/</span>
      <ClockChip ms={oppMs} running={oppRunning} atMs={clock.at_ms} now={now} />
    </span>
  );
}

/**
 * 盤の上 (相手) / 下 (自分) に 1 つずつ置く残時間。 枠も帯も付けず、
 * 走っている側だけ墨が濃くなる。 時間切れの claim は自分側の 1 インスタンスだけが送る。
 *
 * boardId 付き = その盤の時計 (複数時計並列)。 なし = マッチ全体の時計。
 * どちらの進行モードでも見た目は同じ 1 行になる。
 */
function SeatClock({
  you,
  which,
  frozen,
  boardId,
  yourSide,
}: {
  you: 'host' | 'guest' | 'spectator' | null;
  which: 'mine' | 'opp';
  frozen: boolean;
  boardId?: number;
  yourSide: Side | null;
}) {
  const clock = useRoom((s) => s.clock) as ClockSnapshotJson | null;
  const now = useTicker(frozen, 250);
  useAutoClaimTimeout(clock, frozen || which === 'opp', now);
  if (!clock?.boards?.length) return null;
  const mine = which === 'mine';
  const c = boardId != null ? clock.boards.find((b) => b.board === boardId) : clock.boards[0];
  if (!c) return null;
  // 盤ごとの時計は board-local Side 視点なので、 自分の slot Side を基準に取る。
  const myIsFirst = boardId != null ? yourSide === 'First' : you === 'host';
  const ms = myIsFirst === mine ? c.host_ms : c.guest_ms;
  const side: Side = myIsFirst === mine ? 'First' : 'Second';
  const running = !frozen && c.running === side;
  return (
    <ClockChip
      ms={ms}
      running={running}
      atMs={clock.at_ms}
      now={now}
      mine={mine}
    />
  );
}

function PerBoardPolicyChip({
  boardId,
  yourSide,
  frozen,
}: {
  boardId: number;
  yourSide: Side | null;
  frozen: boolean;
}) {
  const clock = useRoom((s) => s.clock) as ClockSnapshotJson | null;
  const now = useTicker(frozen, 500);
  const cell = clock?.boards.find((b) => b.board === boardId);
  if (!cell || !clock) return null;
  const myMs = yourSide === 'First' ? cell.host_ms : cell.guest_ms;
  const oppMs = yourSide === 'First' ? cell.guest_ms : cell.host_ms;
  const myRunning = !frozen && cell.running === yourSide;
  const oppRunning = !frozen && cell.running != null && cell.running !== yourSide;
  return (
    <span className="tab-clock" data-testid={`tab-clock-${boardId}`}>
      <ClockChip ms={myMs} running={myRunning} atMs={clock.at_ms} now={now} mine compact />
      <span className="tab-clock-sep">/</span>
      <ClockChip ms={oppMs} running={oppRunning} atMs={clock.at_ms} now={now} compact />
    </span>
  );
}

function ClockBar({
  yourSide,
  hostName,
  guestName,
  frozen,
}: {
  yourSide: Side | null;
  hostName: string;
  guestName: string;
  frozen: boolean;
}) {
  const clock = useRoom((s) => s.clock) as ClockSnapshotJson | null;
  // PerBoardPolicy の snapshot は board-local Side 視点 (= host_ms = この盤の Side::First)。
  // ホスト名と紐付けるには board の first_seat と state.host_seat の組合せで決まる
  // 「ホスト側 Side」 を求める必要がある (docs/CLOCK.md §7)。
  const snapshot = useRoom((s) => s.snapshot) as {
    boards?: { board: number; first_seat?: 'Leader' | 'Follower' }[];
    host_seat?: 'Leader' | 'Follower';
  } | null;
  const hostSeat = snapshot?.host_seat ?? 'Leader';
  const now = useTicker(frozen, 250);
  useAutoClaimTimeout(clock, frozen, now);
  if (!clock?.boards) return null;
  return (
    <div className="clock-bar">
      {clock.boards.map((b) => {
        const entry = snapshot?.boards?.find((bx) => bx.board === b.board);
        const firstSeat = entry?.first_seat ?? 'Leader';
        // ホスト側の board-local Side: first_seat==host_seat ⇒ host が Side::First を担う
        const hostSide: Side = firstSeat === hostSeat ? 'First' : 'Second';
        const hostMs = hostSide === 'First' ? b.host_ms : b.guest_ms;
        const guestMs = hostSide === 'First' ? b.guest_ms : b.host_ms;
        const hostRunning = !frozen && b.running === hostSide;
        const guestRunning = !frozen && b.running != null && b.running !== hostSide;
        return (
          <div key={b.board} className="clock-cell">
            <ClockSide
              label={hostName}
              sublabel={yourSide === hostSide ? 'あなた' : null}
              side={hostSide}
              ms={hostMs}
              running={hostRunning}
              atMs={clock.at_ms}
              now={now}
              mine={yourSide === hostSide}
            />
            <span className="vs">/</span>
            <ClockSide
              label={guestName}
              sublabel={yourSide != null && yourSide !== hostSide ? 'あなた' : null}
              side={hostSide === 'First' ? 'Second' : 'First'}
              ms={guestMs}
              running={guestRunning}
              atMs={clock.at_ms}
              now={now}
              mine={yourSide != null && yourSide !== hostSide}
            />
          </div>
        );
      })}
    </div>
  );
}

function ClockChip({
  ms: baseMs,
  running,
  atMs,
  now,
  mine,
  compact,
}: {
  ms: number;
  running: boolean;
  atMs: number;
  now: number;
  mine?: boolean;
  compact?: boolean;
}) {
  const remain = displayedMs(baseMs, running, atMs, now);
  const urgency = urgencyOf(remain);
  return (
    <span
      className={`clock-chip clock-chip--${urgency} ${mine ? 'mine' : 'opp'} ${
        running ? 'running' : ''
      } ${compact ? 'compact' : ''}`}
    >
      {urgency === 'flag' ? '0:00' : formatMs(remain)}
    </span>
  );
}

function ClockSide({
  label,
  sublabel,
  side,
  ms: baseMs,
  running,
  atMs,
  now,
  mine,
}: {
  label: string;
  sublabel?: string | null;
  side: Side;
  ms: number;
  running: boolean;
  atMs: number;
  now: number;
  mine: boolean;
}) {
  const remain = displayedMs(baseMs, running, atMs, now);
  const urgency = urgencyOf(remain);
  return (
    <span
      className={`clock-side ${running ? 'running' : ''} ${mine ? 'mine' : 'opp'} clock-side--${urgency}`}
      data-side={side}
    >
      <span className="clock-side-label">
        {label}
        {sublabel && <span className="clock-side-sublabel"> · {sublabel}</span>}
      </span>
      <span className="clock-side-time">
        {urgency === 'flag' ? '時間切れ' : formatMs(remain)}
      </span>
    </span>
  );
}

function displayedMs(baseMs: number, isRunning: boolean, atMs: number, nowMs: number): number {
  if (!isRunning) return baseMs;
  const elapsed = Math.max(0, nowMs - atMs);
  return Math.max(0, baseMs - elapsed);
}

function urgencyOf(remain: number): 'flag' | 'critical' | 'low' | 'ok' {
  if (remain <= 0) return 'flag';
  if (remain <= 10_000) return 'critical';
  if (remain <= 30_000) return 'low';
  return 'ok';
}

function formatMs(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor(sec / 60) % 60;
  const s = sec % 60;
  // 1 時間以上は h:mm:ss (CPU 戦の 2 時間が「120:02」 になるのを避ける)。
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${Math.floor(sec / 60)}:${s.toString().padStart(2, '0')}`;
}
